/**
 * Standard errors of the four estimates students most often need one for.
 *
 * - Mean: SE = s / √n.
 * - Proportion: SE = √(p̂(1 − p̂) / n) — the Wald form a confidence interval uses.
 * - Difference between two independent means: unpooled √(s₁²/n₁ + s₂²/n₂) (Welch), and
 *   the pooled sₚ√(1/n₁ + 1/n₂) that Student's equal-variance t-test uses.
 * - Difference between two proportions: unpooled √(p̂₁q̂₁/n₁ + p̂₂q̂₂/n₂) for an
 *   interval, and pooled √(p̄q̄(1/n₁ + 1/n₂)) for the z-test of H₀: p₁ = p₂.
 */

export function standardErrorOfMean(sd: number, n: number): number {
  return sd / Math.sqrt(n);
}

export function standardErrorOfProportion(p: number, n: number): number {
  return Math.sqrt((p * (1 - p)) / n);
}

export interface MeanDifferenceSe {
  /** √(s₁²/n₁ + s₂²/n₂). */
  unpooled: number;
  /** √(((n₁ − 1)s₁² + (n₂ − 1)s₂²) / (n₁ + n₂ − 2)). */
  pooledSd: number;
  /** sₚ × √(1/n₁ + 1/n₂). */
  pooled: number;
}

export function standardErrorOfMeanDifference(
  sd1: number,
  n1: number,
  sd2: number,
  n2: number
): MeanDifferenceSe {
  const unpooled = Math.sqrt((sd1 * sd1) / n1 + (sd2 * sd2) / n2);
  const pooledSd = Math.sqrt(
    ((n1 - 1) * sd1 * sd1 + (n2 - 1) * sd2 * sd2) / (n1 + n2 - 2)
  );
  return {
    unpooled,
    pooledSd,
    pooled: pooledSd * Math.sqrt(1 / n1 + 1 / n2),
  };
}

export interface ProportionDifferenceSe {
  unpooled: number;
  /** p̄ = (p̂₁n₁ + p̂₂n₂) / (n₁ + n₂), the pooled proportion under H₀. */
  pooledProportion: number;
  pooled: number;
}

export function standardErrorOfProportionDifference(
  p1: number,
  n1: number,
  p2: number,
  n2: number
): ProportionDifferenceSe {
  const unpooled = Math.sqrt((p1 * (1 - p1)) / n1 + (p2 * (1 - p2)) / n2);
  const pooledProportion = (p1 * n1 + p2 * n2) / (n1 + n2);
  return {
    unpooled,
    pooledProportion,
    pooled: Math.sqrt(
      pooledProportion * (1 - pooledProportion) * (1 / n1 + 1 / n2)
    ),
  };
}
